import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Pagination,
    Card,
    CardContent,
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import Swal from "sweetalert2";

const PAGE_SIZE = 10;

function Otherexpense() {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    const [expenses, setExpenses] = useState([]);
    const [expenseHeads, setExpenseHeads] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        expenseHeadId: "",
        date: new Date().toISOString().split("T")[0],
        amount: "",
        description: "",
        userId: currentUser?.uid || "",
    });

    // Fetch expense heads
    useEffect(() => {
        const fetchExpenseHeads = async () => {
            const snap = await getDocs(collection(db, "expenseHeads"));
            setExpenseHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchExpenseHeads();
    }, []);

    // Fetch other expenses
    useEffect(() => {
        const fetchExpenses = async () => {
            const snap = await getDocs(collection(db, "otherExpenses"));
            setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchExpenses();
    }, []);

    const handleChange = e => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!form.expenseHeadId || !form.date || !form.amount) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        try {
            const saveData = {
                ...form,
                userId: currentUser?.uid || "",
                date: Timestamp.fromDate(new Date(form.date)),
            };

            if (editingId) {
                await updateDoc(doc(db, "otherExpenses", editingId), saveData);
                Swal.fire("Success", "Expense updated", "success");
            } else {
                await addDoc(collection(db, "otherExpenses"), saveData);
                Swal.fire("Success", "Expense added", "success");
            }

            setOpenDialog(false);
            setEditingId(null);
            setForm({
                expenseHeadId: "",
                date: new Date().toISOString().split("T")[0],
                amount: "",
                description: "",
                userId: currentUser?.uid || "",
            });

            // Reload data
            const snap = await getDocs(collection(db, "otherExpenses"));
            setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const handleEdit = exp => {
        setEditingId(exp.id);
        setForm({
            expenseHeadId: exp.expenseHeadId,
            date: exp.date?.toDate().toISOString().split("T")[0],
            amount: exp.amount,
            description: exp.description || "",
            userId: exp.userId || currentUser?.uid || "",
        });
        setOpenDialog(true);
    };

    const handleDelete = async id => {
        const res = await Swal.fire({
            title: "Delete expense?",
            icon: "warning",
            showCancelButton: true,
        });
        if (!res.isConfirmed) return;
        await deleteDoc(doc(db, "otherExpenses", id));
        Swal.fire("Deleted", "Expense removed", "success");
        const snap = await getDocs(collection(db, "otherExpenses"));
        setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    // Filter & paginate
    const filtered = expenses.filter(e =>
        e.description?.toLowerCase().includes(search.toLowerCase()) ||
        (expenseHeads.find(h => h.id === e.expenseHeadId)?.name || "")
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box p={2} bgcolor="#f4f6f8" minHeight="100vh">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={800}>
                    Other Expenses
                </Typography>
                <Button variant="contained" onClick={() => setOpenDialog(true)}>
                    Add New Expense
                </Button>
            </Box>

            <TextField
                label="Search by head or description"
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Expense Head</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((e, index) => (
                                <TableRow key={e.id}>
                                    <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                    <TableCell>
                                        {expenseHeads.find(h => h.id === e.expenseHeadId)?.name || e.expenseHeadId}
                                    </TableCell>
                                    <TableCell>{e.date?.toDate().toLocaleDateString()}</TableCell>
                                    <TableCell>{e.amount}</TableCell>
                                    <TableCell>{e.description || "-"}</TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleEdit(e)}>
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(e.id)}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={Math.ceil(filtered.length / PAGE_SIZE)}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* ================= Dialog ================= */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? "Edit Expense" : "Add New Expense"}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Expense Head</InputLabel>
                        <Select name="expenseHeadId" value={form.expenseHeadId} onChange={handleChange}>
                            {expenseHeads.map(h => (
                                <MenuItem key={h.id} value={h.id}>
                                    {h.name || h.id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Date"
                        name="date"
                        type="date"
                        value={form.date}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Amount"
                        name="amount"
                        type="number"
                        value={form.amount}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {editingId ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Otherexpense;